import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { OutlinedIcons } from '@marageti/z4-lib';
import { CoTravelerSummary } from '@marageti/z4-sdk/lib/people';
import { formatCountriesLabel } from './utils';

const { OutlinedUserGroupIcon, OutlinedXIcon } = OutlinedIcons;

// ---------------------------------------------------------------------------
// TripInfo
// ---------------------------------------------------------------------------

type TripInfoProps = { traveler: CoTravelerSummary };

const TripInfo = ({ traveler }: TripInfoProps) => {
  const { trips } = traveler;
  const [singleTrip] = trips;

  if (trips.length === 1) {
    const label = formatCountriesLabel(singleTrip.countries);

    if (singleTrip.countries.length === 1) {
      return (
        <Typography variant="body" color="text.secondary">
          {label}
          {', '}
          {singleTrip.tripDate}
        </Typography>
      );
    }

    return (
      <Tooltip title={singleTrip.countries.join(', ')} placement="top" arrow>
        <Typography variant="body" color="text.secondary" sx={{ cursor: 'default' }}>
          {label}
          {', '}
          {singleTrip.tripDate}
        </Typography>
      </Tooltip>
    );
  }

  const tooltipContent = (
    <Stack spacing={0.5} sx={{ p: 0.5 }}>
      {trips.map((t, i) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          sx={{
            display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: '0.75rem', color: 'inherit',
          }}
        >
          <Box component="span">{t.countries.join(', ')}</Box>
          <Box component="span" sx={{ flexShrink: 0 }}>{t.tripDate}</Box>
        </Box>
      ))}
    </Stack>
  );

  return (
    <Tooltip title={tooltipContent} placement="top" arrow>
      <Typography variant="body" color="text.secondary" sx={{ cursor: 'default' }}>
        {trips.length}
        {' Trips'}
      </Typography>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// CoTravelersModal
// ---------------------------------------------------------------------------

type CoTravelersModalProps = {
  open: boolean;
  onClose: () => void;
  coTravelers: CoTravelerSummary[];
};

const CoTravelersModal = ({ open, onClose, coTravelers }: CoTravelersModalProps) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pt: 3,
        px: 3,
        pb: 1,
      }}
    >
      <Typography variant="h3">Travel with</Typography>
      <IconButton onClick={onClose} size="small" aria-label="close">
        <OutlinedXIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ px: 3, pb: 3 }}>
      <Stack spacing={0}>
        {coTravelers.map((traveler) => (
          <Stack
            key={traveler.id}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ py: 0.5 }}
          >
            <Typography
              variant="body"
              component={Link}
              to={`/travelers/${traveler.id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              sx={{
                color: 'text.primary',
                textDecoration: 'underline',
                '&:hover': { color: 'primary.main' },
              }}
            >
              {traveler.fullName}
            </Typography>
            <TripInfo traveler={traveler} />
          </Stack>
        ))}
      </Stack>
    </DialogContent>
  </Dialog>
);

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

export type TravelWithSectionViewProps = {
  resolved: CoTravelerSummary[];
  visible: CoTravelerSummary[];
  hiddenCount: number;
  isModalOpen: boolean;
  onOpenModal: () => void;
  onCloseModal: () => void;
};

const TravelWithSectionView = ({
  resolved,
  visible,
  hiddenCount,
  isModalOpen,
  onOpenModal,
  onCloseModal,
}: TravelWithSectionViewProps) => (
  <>
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <OutlinedUserGroupIcon fontSize="small" />
        <Typography variant="body">Travel with</Typography>
      </Stack>

      {resolved.length === 0 && (
        <Typography variant="body" color="common.black50">Not recorded</Typography>
      )}

      {resolved.length > 0 && (
        <Stack spacing={0}>
          {visible.map((traveler) => (
            <Stack
              key={traveler.id}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ py: 0.25 }}
            >
              <Typography
                variant="body"
                component={Link}
                to={`/travelers/${traveler.id}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'text.primary',
                  textDecoration: 'underline',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {traveler.fullName}
              </Typography>
              <TripInfo traveler={traveler} />
            </Stack>
          ))}
        </Stack>
      )}

      {hiddenCount > 0 && (
        <Button
          variant="text"
          size="small"
          onClick={onOpenModal}
          sx={{ alignSelf: 'flex-start', p: 0, minWidth: 0 }}
        >
          <Typography variant="captionBold" sx={{ textDecoration: 'underline' }}>
            {`+ ${hiddenCount} Traveler${hiddenCount !== 1 ? 's' : ''}`}
          </Typography>
        </Button>
      )}
    </Stack>

    <CoTravelersModal
      open={isModalOpen}
      onClose={onCloseModal}
      coTravelers={resolved}
    />
  </>
);

export default TravelWithSectionView;
